import { METHOD, normalizePensionMethod } from '../settings/constants'

export const listToDict = list => {
  const dict = {}

  list.forEach((element) => dict[element.name] = element.value)

  return dict
}

export const stringToList = (value, separator) => {
  return value.split(separator)
}

export const formatCurrency = (value) => {
  if (value == null || value === '') {
    return '—'
  }

  const numeric = Number(value)

  if (!Number.isFinite(numeric)) {
    return '—'
  }

  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
  }).format(numeric)
}

export const formatDateTime = (value) => {
  if (!value) return '';

  const date = new Date(value);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

export const formatDateOnly = (value) => {
  if (!value) return '';

  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

/**
 * Human date for Chile: "hoy", "ayer", "6 de agosto", "6 de agosto de 2025".
 */
export const formatFriendlyDate = (value) => {
  if (!value) return ''

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const today = new Date()
  const dayDiff = Math.round(
    (startOfLocalDay(date) - startOfLocalDay(today)) / 86400000
  )

  if (dayDiff === 0) return 'hoy'
  if (dayDiff === -1) return 'ayer'

  const sameYear = date.getFullYear() === today.getFullYear()
  return new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date)
}

/** Payment confirmation line: "Pagado hoy", "Pagado el 6 de agosto". */
export const formatPaidOnLabel = (value) => {
  if (!value) return 'Pagado'
  const friendly = formatFriendlyDate(value) || String(value)
  if (friendly === 'hoy' || friendly === 'ayer') return `Pagado ${friendly}`
  if (friendly.startsWith('Pagado')) return friendly
  return `Pagado el ${friendly}`
}

/** Short caption under a Pagado badge: "Hoy", "Ayer", "El 6 de agosto". */
export const formatPaidDateCaption = (value) => {
  if (!value) return ''
  const friendly = formatFriendlyDate(value) || String(value)
  if (friendly === 'hoy') return 'Hoy'
  if (friendly === 'ayer') return 'Ayer'
  if (friendly.startsWith('El ') || friendly.startsWith('Hoy') || friendly.startsWith('Ayer')) {
    return friendly
  }
  return `El ${friendly}`
}

// formatMonth(value)
// Date-only month strings (e.g. "2025-11-01") are UTC calendar months.
// Format in UTC so Chile (UTC-3/-4) does not shift 1 Sep to August.
export const formatMonth = (value) => {
  if (!value) return ''

  const raw = typeof value === 'string' ? value.slice(0, 10) : null
  const date =
    raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? new Date(`${raw}T00:00:00.000Z`)
      : value instanceof Date
        ? value
        : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const month = new Intl.DateTimeFormat('es-CL', {
    month: 'long',
    timeZone: 'UTC',
  }).format(date)
  return month.charAt(0).toUpperCase() + month.slice(1)
};

/**
 * YYYY-MM key for a date/ISO month string (UTC for date-only values).
 */
export const getMonthKey = (value) => {
  if (!value) return null
  const raw = typeof value === 'string' ? value.slice(0, 10) : null
  if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw.slice(0, 7)
  }
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

/** YYYY-MM for the user's current local calendar month. */
export const getCalendarMonthKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** YYYY-MM for America/Santiago (pension month clock). */
export const getSantiagoMonthKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(d)
  const year = parts.find((part) => part.type === 'year')?.value
  const month = parts.find((part) => part.type === 'month')?.value
  if (!year || !month) return null
  return `${year}-${month}`
}

/** True when monthKey (YYYY-MM) is after the current local calendar month. */
export const isFutureMonthKey = (monthKey, now = new Date()) => {
  if (!monthKey) return false
  const current = getCalendarMonthKey(now)
  if (!current) return false
  return monthKey > current
}

/** True when monthKey is the current Santiago month or later. */
export const isCurrentOrUpcomingMonthKey = (monthKey, now = new Date()) => {
  if (!monthKey) return false
  const current = getSantiagoMonthKey(now)
  if (!current) return false
  return monthKey >= current
}

/**
 * Relative time in es-CL ("hace 3 horas"), falling back to an absolute stamp.
 */
export const formatRelativeFromNow = (value) => {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const diffSec = Math.round((date.getTime() - Date.now()) / 1000)
  const abs = Math.abs(diffSec)
  const rtf = new Intl.RelativeTimeFormat('es-CL', { numeric: 'auto' })

  if (abs < 60) return rtf.format(Math.round(diffSec), 'second')
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour')
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSec / 86400), 'day')
  return formatDateTime(value)
}

/**
 * Best-effort browser / OS label from a User-Agent. Admin-only display.
 */
export const parseUserAgent = (ua) => {
  if (!ua) return { label: 'Dispositivo desconocido', mobile: false }

  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua)
  let browser = 'Navegador'
  if (/Edg\//.test(ua)) browser = 'Edge'
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome'
  else if (/Firefox\//.test(ua)) browser = 'Firefox'
  else if (/Safari\//.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari'

  let os = ''
  if (/iPhone|iPad/.test(ua)) os = 'iOS'
  else if (/Android/.test(ua)) os = 'Android'
  else if (/Mac OS X/.test(ua)) os = 'macOS'
  else if (/Windows/.test(ua)) os = 'Windows'
  else if (/Linux/.test(ua)) os = 'Linux'

  const parts = [browser, os].filter(Boolean)
  if (mobile) parts.push('móvil')
  return { label: parts.join(' · '), mobile }
}

export const formatPensionFormula = ({
  method,
  percentage,
  amount,
  referenceValue,
  calculatedAmount,
}) => {
  const result =
    typeof calculatedAmount === 'string'
      ? calculatedAmount
      : formatCurrency(calculatedAmount)
  const ref = formatCurrency(referenceValue)
  const normalized = normalizePensionMethod(method)

  if (normalized === METHOD.IMM) {
    const pct = percentage == null || percentage === '' ? '—' : percentage
    return `${ref} × ${pct}% = ${result}`
  }
  if (normalized === METHOD.FIXED_AMOUNT) {
    return amount != null && amount !== '' ? formatCurrency(amount) : result
  }
  const qty = percentage == null || percentage === '' ? '—' : percentage
  return `${ref} × ${qty} UTM = ${result}`
}

export const PENSION_PERIOD_STATUS = {
  PAID: 'paid',
  UPCOMING: 'upcoming',
  DUE: 'due',
  OVERDUE: 'overdue',
}

/**
 * Lifecycle of a pension period relative to the calendar month.
 * @param {{ month?: string|Date|null, monthKey?: string|null, status?: string|null }} params
 * @returns {'paid'|'upcoming'|'due'|'overdue'}
 */
export const resolvePensionPeriodStatus = ({
  month,
  monthKey,
  status,
  now = new Date(),
} = {}) => {
  if (status === 'payed') return PENSION_PERIOD_STATUS.PAID

  const key = monthKey || getMonthKey(month)
  const current = getCalendarMonthKey(now)

  if (key && current) {
    if (key > current) return PENSION_PERIOD_STATUS.UPCOMING
    if (key < current) return PENSION_PERIOD_STATUS.OVERDUE
  }

  return PENSION_PERIOD_STATUS.DUE
}
